<template>
  <Component
    :is="isValidDownload ? SharedBaseDownload : SharedBaseLink"
    v-if="isValidDownload || isValidUrl || hasOverlayLayer || $attrs?.['onClick']"
    v-editable="blok"
    :link="isValidDownload || hasOverlayLayer ? undefined : blok.link"
    :asset="isValidDownload ? (asset ?? undefined) : undefined"
    :filename="isValidDownload ? blok.text : undefined"
    class="util-link-item-icon"
    :class="[isDark ? 'is-dark' : 'is-light', { 'is-disabled': isDisabled }]"
    :aria-label="isTextHiddenOnMobile ? blok.text : undefined"
    @click="onButtonClick"
    @mouseenter="onIconMouseOver"
    @mouseleave="onIconMouseOut">
    <BaseIcon v-if="icon && iconPosition === 'start'" :name="icon" color="inherit" class="icon-start" />
    <div v-if="hasText" class="text" :class="{ 'is-hidden-on-mobile': isTextHiddenOnMobile }">
      <span class="main-text">
        <span>{{ blok.text }}</span>
        <BaseIcon v-if="icon && iconPosition === 'end'" :name="icon" color="inherit" class="icon-end" />
      </span>
      <span v-if="subtext" class="subtext" :class="isDark ? 'is-dark' : 'is-light'">
        {{ subtext }}
      </span>
    </div>
  </Component>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SharedBaseDownload, SharedBaseLink } from '@seb-platform/shared/components/base';
import { useIconAnimation, useLayer } from '@seb-platform/shared/composables';
import { urlExtractFromLink, urlIsExternal } from '@seb-platform/shared/utils/url';
import { BaseIcon } from '~/components/base';
import { useGtmTracking } from '~/composables/useGtmTracking';
import type { Locale } from '~/types/locales';
import type { StoryblokAsset, UtilLinkItemIconStoryblok } from '~/types/storyblok-generated';
import {
  ICON_DOWNLOAD,
  ICON_EXTERNAL,
  ICON_FACEBOOK,
  ICON_INSTAGRAM,
  ICON_INTERNAL,
  ICON_LINKEDIN,
  ICON_MAIL,
  ICON_OVERLAY,
  ICON_PHONE,
  ICON_TIKTOK,
  ICON_VIMEO,
  ICON_WECHAT,
  ICON_XIAOHONGSHU,
  ICON_XING,
  ICON_YOUTUBE,
} from '~/utils/icon';
import { urlIsContact } from '~/utils/url';

const props = defineProps<{
  blok: Omit<UtilLinkItemIconStoryblok, 'icon' | 'icon_position'>; // TODO remove Omit once the fields were actually removed from Storyblok
  forceIcon?: string;
  forceIconPosition?: 'start' | 'end';
  isDark?: boolean;
  isDisabled?: boolean;
  isTextHiddenOnMobile?: boolean;
  subtext?: string;
  asset?: StoryblokAsset | null;
  detectIcon?: boolean;
  hideInternalIcon?: boolean;
  trackClick?: boolean;
}>();

const { locale } = useI18n();
const { onIconMouseOut, onIconMouseOver } = useIconAnimation();
const { state: layerState } = useLayer();
const { trackEvent } = useGtmTracking();

const hasText = computed(() => !!props.blok.text);
const isValidDownload = computed(() => !!props.asset?.filename);
const isValidUrl = computed(
  () => props.blok.type === 'link' && !!props.blok.link && !!urlExtractFromLink(props.blok.link)
);
const hasOverlayLayer = computed(() => props.blok.type === 'overlay' && typeof props.blok.item_overlay === 'object');

const icon = computed(() => {
  if (props.detectIcon) {
    if (props.blok.link?.url.includes('tel:')) {
      return ICON_PHONE;
    }

    if (props.blok.link?.url.includes('youtube')) {
      return ICON_YOUTUBE;
    }

    if (
      props.blok.link?.url.includes('mailto:') ||
      (!!props.blok.link && urlIsContact(props.blok.link, locale.value as Locale))
    ) {
      return ICON_MAIL;
    }

    if (props.blok.link?.url.includes('linkedin')) {
      return ICON_LINKEDIN;
    }

    if (props.blok.link?.url.includes('facebook')) {
      return ICON_FACEBOOK;
    }

    if (props.blok.link?.url.includes('vimeo')) {
      return ICON_VIMEO;
    }

    if (props.blok.link?.url.includes('insta')) {
      return ICON_INSTAGRAM;
    }

    if (props.blok.link?.url.includes('xing')) {
      return ICON_XING;
    }

    if (props.blok.link?.url.includes('weixin') || props.blok.link?.url.includes('wechat')) {
      return ICON_WECHAT;
    }

    if (props.blok.link?.url.includes('xiaohongshu')) {
      return ICON_XIAOHONGSHU;
    }

    if (props.blok.link?.url.includes('tiktok') || props.blok.link?.url.includes('douyin')) {
      return ICON_TIKTOK;
    }
  }

  if (props.forceIcon) {
    return props.forceIcon;
  }
  if (hasOverlayLayer.value) {
    return ICON_OVERLAY;
  }
  if (/https:\/\/a\.storyblok\.com\/f\//.test(props.blok.link?.url ?? '') === true) {
    // TODO this is a temporary solution to fix custom download icon placements, the module should be adjusted to allow the use of 'util_link-item-subtext' component
    return ICON_DOWNLOAD;
  }
  if (props.blok.link?.linktype === 'url' && urlIsExternal(props.blok.link.url)) {
    return ICON_EXTERNAL;
  } else if (!props.hideInternalIcon) {
    return ICON_INTERNAL;
  }

  return null;
});

const iconPosition = computed<'start' | 'end'>(() => {
  if (icon.value === ICON_DOWNLOAD) {
    // TODO this is a temporary solution to fix custom download icon placements, the module should be adjusted to allow the use of 'util_link-item-subtext' component
    return 'start';
  }
  if (icon.value === ICON_INTERNAL || icon.value === ICON_EXTERNAL || icon.value === ICON_OVERLAY) {
    return 'end';
  }
  return props.forceIconPosition ?? 'end';
});

function onButtonClick(e: MouseEvent) {
  if (props.trackClick) {
    trackEvent({ event: 'navigationClick', navigationItem: props.blok.text });
  }
  if (!props.blok.item_overlay || typeof props.blok.item_overlay === 'string') {
    // Not an overlay or invalid linked overlay page
    return;
  }
  e.preventDefault();
  layerState.corePage = props.blok.item_overlay;
}
</script>

<style src="./UtilLinkItemIcon.scss" lang="scss" scoped />
